/**
 * Bridges the browse endpoint's keyset cursors to the table's page index.
 *
 * The API pages by cursor — "give me what follows product X" — while the shared
 * `DataTable` thinks in page numbers. The two only reconcile because the table
 * is rendered with `hidePageNumbers`, so navigation is Prev/Next and the index
 * can only move one step at a time. That is what makes a cursor per visited
 * page sufficient: page N's cursor is always known by the time page N can be
 * asked for, and a jump straight to page 347 (which has no cursor, and cannot
 * have one without reading everything before it) is not reachable.
 *
 * `cursors[i]` is the cursor that fetches page `i`. Index 0 is always `null` —
 * the first page has nothing before it.
 */
export type CursorStack = readonly (string | null)[];

/** A fresh stack, positioned at the first page. */
export const initialCursors: CursorStack = [null];

/**
 * The cursor for *pageIndex*, or `undefined` when that page has never been
 * reached and so has no cursor. `undefined` is the meaningful case: it means
 * "not reachable", not "start from the beginning", and conflating the two would
 * silently serve page one's rows under a later page's number.
 */
export function cursorForPage(cursors: CursorStack, pageIndex: number): string | null | undefined {
  return cursors[pageIndex];
}

/**
 * Records the cursor that leads *past* `pageIndex`, so the next page becomes
 * reachable.
 *
 * Only ever appends the immediately-next entry, and only when it is absent: a
 * re-render or a cache hit re-reports the same `nextCursor` for a page already
 * recorded, and appending again would push the stack out of step with the page
 * numbers it indexes.
 */
export function withNextCursor(
  cursors: CursorStack,
  pageIndex: number,
  nextCursor: string | null,
): CursorStack {
  if (nextCursor === null) return cursors;
  if (cursors.length !== pageIndex + 1) return cursors;
  return [...cursors, nextCursor];
}

/** Whether a next page has a cursor and can therefore be fetched. */
export function canGoToNextPage(cursors: CursorStack, pageIndex: number): boolean {
  return cursors[pageIndex + 1] !== undefined;
}
